import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {createUUId, exportCSVFile, formatMoney, numComparator} from "../../../../../../../core/services/utils/utils";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import axios from "axios";
import BulkPriceChangeReportAttemptSummary from "./summary";
import BulkPriceChangeReportProducts from "./products";
import ApiResponseCodes from "../../../../../../../core/models/static/api-response-codes";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import ToastService from "../../../../../../../core/services/toast";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import {useHistory, useLocation, useParams} from "react-router-dom";
import useQuery from "../../../../../../hooks/use-query";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const generalAttemptId = "General";

const BulkPriceChangeReport = () => {
    const history = useHistory()
    const params = useParams()
    const location = useLocation();
    const [query, setQuery] = useQuery()
    const [loading, setLoading] = useState(false);
    const [state, setState] = useState(null);
    const [trying, setTrying] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [starting, setStarting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [newLogs, setNewLogs] = useState([]);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<import('axios').CancelTokenSource>}*/
    const cancelTokenSourceRef = useRef();
    const {configureNotebookTags} = useConfigureNotebook('bulk-price-change-report-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportBulkPriceChangeReport = operatorAccessLevels.packages.lookup.exportBulkPriceChangeReport;
    const {getPriceLevelDictionary, priceLevelDict} = useCachedPriceLevelsDictionaryController({loadDict: true});

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.bulkPriceChangeReport(params.id));
        configureBehavioralTool((tags) => tags.bulkPriceChangeReport(params.id));
    }, [configureBehavioralTool, configureNotebookTags, params.id]);

    const requestId = useMemo(() => Number(params.id), [params.id])

    const generalAttempt = useMemo(() => {
        const affectedParts = state?.totalAffectedParts ?? 0;
        const successfulParts = state?.tries?.reduce((agg, c) => agg + c.successfulParts, 0) ?? 0;
        const failedParts = affectedParts - successfulParts;
        const lastAttempt = state?.tries?.[state?.tries?.length - 1];
        return {
            id: generalAttemptId,
            index: 0,
            affectedParts: trying ? lastAttempt?.affectedParts : affectedParts,
            successfulParts: trying ? lastAttempt?.successfulParts : successfulParts,
            failedParts: trying ? lastAttempt?.failedParts : failedParts,
        }
    }, [state?.totalAffectedParts, state?.tries, trying])

    const summaryInfo = useMemo(() => ({
        submitDateTime: !!state?.submitDateTime ? DateUtils.serverToLocal(state.submitDateTime).format(DateUtils.dateFormat) : '--',
        operatorName: !!state?.operatorName ? state.operatorName : '---',
        priceChange: typeof state?.requestedAmount !== 'undefined' && typeof state?.isPercentage !== 'undefined'
            ? `${!state.isPercentage
                ? `${state.requestedAmount > 0 ? '+' : '-'}` + formatMoney(Math.abs(state.requestedAmount))
                : `${state.requestedAmount > 0 ? '+' : ''}` + state.requestedAmount?.toFixed(2) + '%'}`
            : '--',
        priceLevels: state?.priceLevels ?? [],
    }), [state?.isPercentage, state?.operatorName, state?.requestedAmount, state?.submitDateTime, state?.priceLevels])

    const tabsList = useMemo(() => [
        {label: "General", value: generalAttemptId},
        ...(state?.tries?.map(e => ({label: `Attempt ${e.index}`, value: e.id})) ?? []),
    ], [state?.tries])

    const tab = useMemo(() => {
        const queryTab = query.tab;
        if (!queryTab || !tabsList.map(e => e.value).includes(Number(queryTab))) {
            if (queryTab === generalAttemptId)
                return queryTab;
            return generalAttemptId;
        }
        return Number(queryTab);
    }, [query, tabsList])

    const selectedAttempt = useMemo(() => {
        if (tab === generalAttemptId)
            return generalAttempt
        return state?.tries?.find(e => e.id === tab);
    }, [generalAttempt, state?.tries, tab])


    const isSelectedAttemptReadonly = useMemo(() => {
        if (selectedAttempt?.id === generalAttemptId)
            return false;
        const index = state?.tries?.findIndex(e => e.id === selectedAttempt?.id);
        if (index === -1)
            return true;
        return index !== (state?.tries?.length ?? 0) - 1;
    }, [selectedAttempt?.id, state?.tries])


    /**
     * With each change in the [query] or [tabsList] values:
     * - ensures there is at least one tab selected.
     * - if the currently selected tab is not among the available tabs, changes the tab to the general tab
     */
    useLayoutEffect(() => {
        if (loading || tabsList.length <= 1)
            return;
        if (!tabsList.map(e => e.value).includes(tab)) {
            changeTab(undefined, generalAttemptId);
        }
    }, [tab, tabsList, loading])

    /**
     * With each change in the [id] of the url params:
     * - fetches the bulk-price-change information form the server.
     */
    useEffect(() => {
        getBulkPriceChangeInformation().then();
    }, [params?.id])

    /**
     * With each change in the [state] value:
     * - if the [autoStart] value of the url query is set to 'true' then automatically starts a change-attempt.
     */
    useEffect(() => {
        if (!state)
            return;
        if (!query?.autoStart)
            return;
        if (query?.autoStart === 'true') {
            history.replace(location.pathname);
            if (state.tries?.length === 1)
                startAttempt().then()
        }
    }, [state])

    /**
     * With each change in the [trying] state value:
     * - if no trying, then aborts the change-price API's abortController to stop it
     * - if trying then starts a new price-change process.
     */
    useEffect(() => {
        if (!trying) {
            return cancelTokenSourceRef.current?.cancel();
        }
        cancelTokenSourceRef.current = axios.CancelToken.source();
        proceedWithPriceChange().then();
    }, [trying])

    /**
     * Fetches the bulk-price-change information form the server.
     * * If the api call was successful, sets the [state] value with the response data.
     */
    const getBulkPriceChangeInformation = async () => {
        setLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getBulkPriceChangeInformation(requestId),
            getPriceLevelDictionary(),
        ])
        if (!isMounted())
            return;
        setLoading(false);
        if (!response?.resultFlag)
            return;
        if (!!response.data) {
            response.data?.tries?.sort((a, b) => numComparator(a.index, b.index));
            response.data.priceLevels = response.data.priceLevels?.map(e => ({
                ...e,
                title: priceLevelDict?.find(p => p.priceLevelId === e.id)?.title ?? e.title
            })) ?? [];
        }
        setState(response.data);
    }

    /**
     * Starts an attempt for the changing the part prices of the current request.
     *
     * * if the last attempt is paused, continues that attempt otherwise creates a new attempt prior to starting it.
     * @return {Promise<void>}
     */
    const startAttempt = async () => {
        setStarting(true);
        if (!!state?.tries?.length) {
            const lastAttempt = state?.tries?.[state?.tries?.length - 1];
            if (lastAttempt.affectedParts === (lastAttempt.successfulParts + lastAttempt.failedParts)) {
                const resultFlag = await createNewAttempt();
                if (!isMounted())
                    return;
                if (!resultFlag) {
                    return setStarting(false);
                }
            }
        } else {
            const resultFlag = await createNewAttempt();
            if (!isMounted())
                return;
            if (!resultFlag) {
                return setStarting(false);
            }
        }
        setStarting(false);
        setTrying(true);
    }

    /**
     * Creates a new attempt for the current request.
     * @return {Promise<void>}
     */
    const createNewAttempt = async () => {
        const forApi = {
            requestId: requestId,
        }
        const response = await Api.createNewBulkPriceChangePriceAttempt(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setState(prevState => ({
                ...prevState,
                tries: [...prevState?.tries ?? [], response.data],
            }))
            changeTab(undefined, response.data.id);
        }
    }

    /**
     * Proceeds with the price change of the parts in the last attempt of this request.
     *
     * * until all the products of the last attempt are attempted for a price change or [trying] state value is set to false:
     *  - calls the [changePrice] callback
     * @return {Promise<void>}
     */
    const proceedWithPriceChange = async () => {
        const lastAttempt = state?.tries?.[state?.tries?.length - 1];
        let changesAttempted = lastAttempt.successfulParts ?? 0;
        while (trying && changesAttempted < lastAttempt.affectedParts) {
            const resultFlag = await changePrice();
            if (!resultFlag) {
                return
            }
            changesAttempted++;
        }
        setTrying(false);
    }

    /**
     * Changes the price of a single product in the last attempt of this request.
     *
     * * if the result of the api is successful, adds the change logs in the state and creates a callback to remove them after 5 seconds.
     * @return {Promise<boolean>}
     */
    const changePrice = async () => {
        const forApi = {
            requestId: requestId,
        }
        if (!isMounted() || !!cancelTokenSourceRef.current?.token?.reason) {
            return false
        }
        const response = await Api.changePartPriceOfBulkPriceChangeRequest(forApi);
        if (response?.resultFlag) {
            const successful = response.data.every(e => e.isSuccess);
            setState(prevState => ({
                ...prevState,
                tries: prevState.tries.map((e, index, array) => index === array.length - 1 ?
                    {
                        ...e,
                        failedParts: !successful ? e.failedParts + 1 : e.failedParts,
                        successfulParts: successful ? e.successfulParts + 1 : e.successfulParts,
                    }
                    : e
                )
            }))
            const lastAttempt = state?.tries?.[state?.tries?.length - 1];
            setNewLogs(prevState => [
                ...prevState,
                ...response.data.map(e => ({
                    ...e,
                    try: {id: lastAttempt.id, index: lastAttempt.index},
                    priceLevelId: e.priceLevelId ?? createUUId(),
                    priceLevelTitle: priceLevelDict?.find(p => p.priceLevelId === e.priceLevelId)?.title ?? e.priceLevelTitle,
                }))
            ])
            setTimeout((list) => {
                setNewLogs(prevState => prevState.filter(e =>
                    !list.find(le =>
                        le.partId === e.partId &&
                        le.priceLevelId === e.priceLevelId &&
                        le.categoryId === e.categoryId &&
                        le.try?.id === e.try?.id &&
                        le.isSuccess === e.isSuccess
                    )))
            }, 10000, response.data)
        }
        return (response?.errorCode !== ApiResponseCodes.bulkPriceChangeAttemptIsFinished);
    }

    /**
     * Cancels the last attempt of the current request.
     * @return {Promise<void>}
     */
    const cancelLastAttempt = async () => {
        const forApi = {
            requestId: requestId,
        }
        setCancelling(true);
        const response = await Api.cancelLastBulkPriceChangePriceAttempt(forApi);
        if (!isMounted())
            return;
        setCancelling(false);
        if (response?.resultFlag) {
            setState(prevState => ({
                ...prevState,
                tries: prevState.tries.map((e, index, array) => {
                    if (index === array.length - 1) {
                        if ((e.affectedParts - e.successfulParts) > 0)
                            createNewAttempt().then();
                        return {...e, failedParts: e.affectedParts - e.successfulParts}
                    }
                    return e
                })
            }))
            setTrying(false);
        }
    }

    /**
     * Exports a detailed report of the products of the given attempt.
     *
     * @param attempt
     * @param onlySuccessful
     * @return {Promise<void>}
     */
    const exportReport = async (attempt, onlySuccessful = null) => {
        if (!canExportBulkPriceChangeReport) {
            return;
        }
        const _tryId = attempt.id === generalAttemptId ? undefined : attempt.id;

        let _pagination = {
            currentPage: 1,
            pageSize: 50,
            length: 0
        }
        let items = [];
        setExporting(true);

        const priceLevelDict = await getPriceLevelDictionary();

        do {

            const forApi = {
                requestId: requestId,
                tryId: attempt.id === generalAttemptId ? undefined : attempt.id,
                isSuccess: onlySuccessful,
                pagination: {
                    currentPage: _pagination.currentPage,
                    pageSize: _pagination.pageSize,
                },
            }

            const response = await Api.getBulkPriceChangePriceChangeLogs(forApi);

            if (!isMounted())
                return;
            if (!response?.resultFlag)
                return;
            if (!response?.data?.items?.length)
                break;

            items.push(...response.data.items);
            _pagination.currentPage = _pagination.currentPage + 1;
            _pagination.length = response.data.totalCount;

        } while (_pagination.length > items.length)

        setExporting(false);

        const entries = items.map((e, index) => ({
            "      ": index + 1,
            "Attempt": e.try.index,
            "Status": e.isSuccess ? "Successful" : e.isCanceled ? "Cancelled" : "Failed",
            "Part No": e.partNumber ?? '',
            "Category": e.categoryName ?? '',
            "Price Level": priceLevelDict?.find(p => p.priceLevelId === e.priceLevelId)?.title ?? e.priceLevelTitle ?? "",
            "Old Price": (!!e.oldPrice || e.oldPrice === 0) ? formatMoney(e.oldPrice) : '',
            "New Price": (!!e.newPrice || e.newPrice === 0) ? formatMoney(e.newPrice) : '',
            "Error Description": e.errorDescription ?? '',
        }))
        if (onlySuccessful !== null) {
            for (const entry of entries) {
                delete entry.Status;
            }
        }
        if (!_tryId) {
            for (const entry of entries) {
                delete entry.Attempt;
            }
        }
        if (!entries.length) {
            ToastService.warning({message: "No records found for exporting!"});
            return;
        }
        const title = `${onlySuccessful === null ? "All" : onlySuccessful === true ? "Successfully Changed" : "Unsuccessfully Changed"} Products Report${!_tryId ? '' : ` for Attempt ${attempt.index}`} of Bulk Price Change`
        exportCSVFile(entries, title);
    }

    /**
     * Pauses the last attempt's price changing process by settings the [trying] state value to false
     */
    const pauseLastAttempt = () => {
        setTrying(false);
    }

    /**
     * Changes the tab of this view with the given tab value.
     * @type {function(*=, *=): void}
     */
    const changeTab = useCallback((e, value) => {
        setQuery(prevState => ({...prevState, tab: value}), {replace: true});
    }, [setQuery])

    return (
        <>
            <div className={'bulk-price-change-report-view'}>
                <p className={'text-lg font-weight-semi-bold mb-3'}>
                    Bulk Price Change Report
                </p>
                {
                    loading &&
                    <div className={'loading new-bulk-price-change mt-3'}>
                        <div/>
                    </div>
                }
                {
                    !loading &&
                    <>
                        <div className={'d-flex w-100 mb-3'}>
                            <div className='w-100 flex-grow-1'>
                                <Tabs value={tab ?? false} onChange={changeTab}>
                                    {
                                        tabsList.map(e => (
                                            <Tab
                                                key={e.value}
                                                value={e.value}
                                                label={e.label}
                                            />
                                        ))
                                    }
                                </Tabs>
                            </div>
                        </div>
                        {
                            selectedAttempt &&
                            <BulkPriceChangeReportAttemptSummary
                                attempt={selectedAttempt}
                                summaryInfo={summaryInfo}
                                trying={trying}
                                starting={starting}
                                cancelling={cancelling}
                                exporting={exporting}
                                readonly={isSelectedAttemptReadonly}
                                start={startAttempt}
                                cancel={cancelLastAttempt}
                                pause={pauseLastAttempt}
                                exportReport={exportReport}
                                canExportReport={canExportBulkPriceChangeReport}
                            />
                        }
                        {
                            selectedAttempt &&
                            <BulkPriceChangeReportProducts
                                showAffectedProducts={selectedAttempt.id === generalAttemptId}
                                newLogs={newLogs}
                                requestId={requestId}
                                attemptId={selectedAttempt.id === generalAttemptId ? undefined : selectedAttempt.id}
                            />
                        }
                    </>
                }
            </div>
        </>
    );
}

export default BulkPriceChangeReport;
